/**
 * PackRouteGuard — route-level gate for /crm/* pages on operator-mode packs.
 *
 * Distinct from RouteGuard (RBAC nav-visibility). RouteGuard does NOT gate
 * /crm/* — getRequiredNavId returns null for /crm/* so RBAC lets it through;
 * pack-installed access is enforced HERE (client-side; the server has its
 * own additive gate — requirePack middleware on /api/v1/crm + /api/v1/skycrm).
 *
 * Phase-0 de-hardcoding: gates on hasOperatorPack (any installed pack with
 * mode 'operator_crm'), not hasPack("senior_living") — the CRM shell belongs
 * to whichever operator pack the tenant installed, so a future
 * behavioral_health pack passes this guard with zero changes here.
 *
 * Decision B:
 *  - Gate on hasOperatorPack ONLY (not currentMode). A user in
 *    Advisor view can deep-link /crm/persons without a toggle click first.
 *  - On mount of a /crm/* route, flip currentMode → 'operator_crm' as a
 *    side effect so the user lands in CRM view automatically.
 *  - No pack → redirect to "/" (existing Sky Advisor home, which a
 *    non-senior-living user can actually use).
 *
 * ## Loading-race fix (Overview UAT BUG #1)
 *
 * On external navigation (bookmark / refresh / shared link / new tab) to
 * any /crm/* URL, the app remounts fresh and PackProvider's
 * `useGetInstalledPacksQuery` is in-flight when this guard first renders.
 * During that window `packs === []` and `hasOperatorPack === false`
 * even for a tenant that DOES have the pack installed. Pre-fix the guard
 * eagerly redirected to "/" before the query settled — the contract's
 * "URL-state survives refresh/bookmark" promise was broken for /crm/persons
 * and any other deep link consumed externally. In-app `navigate()` calls
 * didn't hit it because the PackContext stays mounted with `packs`
 * already populated.
 *
 * Fix: defer the redirect decision while `isLoading`. Render the same
 * spinner pattern as CrmLandingRedirect for visual consistency. The
 * `setCurrentMode("operator_crm")` side effect waits for `allowed` to
 * settle, so it still only fires for genuine pack-installed users.
 */
import { type ReactNode, useEffect } from "react";
import { Navigate } from "react-router";
import { usePacks } from "./packs/PackContext";

export function PackRouteGuard({ children }: { children: ReactNode }) {
  const { hasOperatorPack, isLoading, setCurrentMode } = usePacks();
  const allowed = hasOperatorPack;

  useEffect(() => {
    if (allowed) setCurrentMode("operator_crm");
  }, [allowed, setCurrentMode]);

  if (isLoading) {
    return (
      <div
        className="p-6 flex justify-center"
        role="status"
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading pack access"
      >
        <div
          className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!allowed) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }
  return <>{children}</>;
}
